import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useFetchMockDetail from '../../Hooks/mockInterview/useFetchMockDetail';

import arrowUpright from '../../Assets/svgs/arrowUpright.svg';
import arrow from '../../Assets/arrow.png';
import { alphanumericDate } from '../../constents/functions';
import videoRecorderIcon from '../../Assets/svgs/Interview/videoRecorderIcon.svg';
import startRecordPreviewImg from '../../Assets/svgs/Interview/startRecoredPreview.svg';
import stopRecordPreviewIcon from '../../Assets/svgs/Interview/stopRecordPreviewIcon.svg';
import previewRecordedSuccessfully from '../../Assets/svgs/Interview/previewRecordedSuccessfully.svg';
import { Loader } from '../../constents/components';

const AllowCameraButton = ({ onClick }) => (
  <div onClick={onClick} className="cursor-pointer flex gap-2 bg-white rounded-full px-4 py-2">
    <img src={videoRecorderIcon} alt="videoRecorderIcon" />
    <span className="text-lg font-semibold">Allow camera</span>
  </div>
);

const PreviewRecordStartButton = ({ onClick }) => (
  <div onClick={onClick} className="cursor-pointer flex gap-2 bg-[#FF5D0E] rounded-full px-4 py-2">
    <img src={startRecordPreviewImg} alt="startRecordPreviewImg" />
    <span className="text-lg text-white font-semibold">Start Recording</span>
  </div>
);

const PreviewRecordingStopButton = ({ onClick }) => (
  <div onClick={onClick} className="cursor-pointer flex gap-2 bg-[#FF5D0E] rounded-full px-4 py-2">
    <img src={stopRecordPreviewIcon} alt="stopRecordPreviewIcon" />
    <span className="text-lg text-white font-semibold">Stop Recording</span>
  </div>
);

const RecordingSuccessStatus = () => (
  <div className="cursor-pointer flex flex-col items-center rounded-full px-4 py-2">
    <img className="w-8" src={previewRecordedSuccessfully} alt="previewRecordedSuccessfully" />
    <h1 className="text-xl text-[#34A853] font-semibold">Video Recorded!</h1>
    <span className="text-lg font-semibold">You can now start your interview</span>
  </div>
);

const MockConsent = ({ onAccepted }) => {
  const navigate = useNavigate();
  const videoElement = useRef(null);

  const [videoStatus, setVideoStatus] = useState(undefined); // 'ready', 'recording', 'recorded'
  const [cameraLoading, setCameraLoading] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [error, setError] = useState(null);

  const mockId = new URLSearchParams(window.location.search).get('mock_id');
  const [detailLoading, detailError, fetchMockDetail] = useFetchMockDetail();

  useEffect(() => {
    if (mockId) fetchMockDetail(mockId);
  }, [mockId]);

  const UserName = 'Your Name';
  const JobTitle = 'Mock Job Title';
  const CompanyName = 'Mock Company';
  const NoOfQuestions = 15;
  const DateOfToday = alphanumericDate(Date.now());
  const EstimatedTimeToCompleteInterview = 20;

  const Heading = 'Before we proceed, we will need your declaration';
  const Description = 'Switch on your camera and recite the following excerpt:';
  const TextToRead = `"Hello, my name is ${UserName}. Today is ${DateOfToday}, and I am about to begin my online interview for ${JobTitle} at ${CompanyName}. I confirm that I am completing this interview by myself without any external help. I understand that the interview will be monitored and recorded to ensure fairness. I am ready to begin."`;

  const NumberOfQuestionText = 'Total Questions: ';
  const TimeRequiredText = 'Estimated Time Required: ';
  const TimeRequiredValue = `${EstimatedTimeToCompleteInterview} mins`;

  const getCameraAndStartPreview = async () => {
    setCameraLoading(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setMediaStream(stream);
      if (videoElement.current) {
        videoElement.current.srcObject = stream;
      }
      setVideoStatus('ready');
    } catch (err) {
      setError('Camera access failed. Please ensure you have granted camera permissions.');
      setVideoStatus(undefined);
    } finally {
      setCameraLoading(false);
    }
  };

  const startRecordingPreview = () => setVideoStatus('recording');

  const stopRecordingPreview = () => {
    setVideoStatus('recorded');
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
  };

  const handleStartInterview = () => {
    if (onAccepted) onAccepted();
  };

  useEffect(() => {
    if (videoElement.current && mediaStream && (videoStatus === 'ready' || videoStatus === 'recording')) {
      videoElement.current.srcObject = mediaStream;
    }
  }, [mediaStream, videoStatus]);

  useEffect(() => {
    return () => {
      if (mediaStream) mediaStream.getTracks().forEach(track => track.stop());
    };
  }, [mediaStream]);

  if (detailLoading) return <Loader />;
  if (detailError) return <div className="flex justify-center items-center min-h-40 text-red-500">Failed to load interview details. Please try again.</div>;

  return cameraLoading ? (
    <Loader />
  ) : (
    <div className="">
      <div className="w-[90vw] lg:w-[80vw] bg-white px-4 py-4 border-[1px] border-gray-200 rounded-2xl">
        <div className="flex items-start">
          <h1 className="flex-1 text-3xl font-semibold">Getting Started with Mock Interview</h1>
          <div className="flex-1 flex justify-end">
            <button className="flex gap-2 items-center px-4 py-2 rounded-full border-[1px] border-gray-300 bg-white">
              <img src={arrowUpright} alt="external-link" className="w-3 h-3" />
              Open in New tab
            </button>
          </div>
        </div>

        <div className="flex justify-between mt-2">
          <p className="text-gray-700">
            {NumberOfQuestionText}
            <span className="text-black font-semibold">{NoOfQuestions}</span>
          </p>
          <p className="text-gray-700">
            {TimeRequiredText}
            <span className="text-black font-semibold">{TimeRequiredValue}</span>
          </p>
        </div>

        <div className="flex flex-col-reverse lg:flex-row gap-12 mt-4 bg-[#F9F9F9] p-4 rounded-2xl">
          <div className="flex-1">
            <h4 className="text-2xl font-bold">{Heading}</h4>
            <p className="text-gray-500 mt-1">{Description}</p>
            <p className="text-gray-600 mt-4 whitespace-pre-wrap">{TextToRead}</p>
          </div>

          <div className="flex-1 relative rounded-2xl overflow-hidden">
            <div className="z-10 absolute top-0 left-0 right-0 bottom-0 flex items-end pb-8 justify-center">
              {videoStatus === undefined && <AllowCameraButton onClick={getCameraAndStartPreview} />}
              {videoStatus === 'ready' && <PreviewRecordStartButton onClick={startRecordingPreview} />}
              {videoStatus === 'recording' && <PreviewRecordingStopButton onClick={stopRecordingPreview} />}
              {videoStatus === 'recorded' && <RecordingSuccessStatus />}
            </div>

            <div className="rounded-2xl overflow-hidden w-full bg-black aspect-video">
              <video
                ref={videoElement}
                muted
                autoPlay
                playsInline
                className={`w-full h-full object-cover ${
                  videoStatus === undefined || videoStatus === 'recorded' ? 'hidden' : 'block'
                }`}
              />
            </div>

            {error && (
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-center bg-red-500 bg-opacity-75 p-4 rounded">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="flex space-x-4 mt-10">
          <button
            className={`px-8 py-3 text-white rounded-full flex items-center ${
              videoStatus === 'recorded' && !detailLoading ? 'bg-black cursor-pointer' : 'bg-gray-300 cursor-not-allowed'
            }`}
            onClick={() => videoStatus === 'recorded' && !detailLoading && handleStartInterview()}
          >
            {detailLoading ? 'Loading...' : 'Go To Instructions'}
            <img src={arrow} alt="external-link" className="w-3 h-3 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MockConsent;
